import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { creditsUsed, displayName, percentUsed, useAccount } from "../account.js";
import { useBackClose } from "../back.js";
import type { UsageWindow } from "../hosted-api.js";
import { formatLocale, useText } from "../i18n.js";
import { usePresence } from "../presence.js";
import { ACCOUNT_MENU } from "../text/account-menu.js";
import { useTimeZone } from "../time-zone.js";

/** The menu's exit takes this long; styles/sidebar.css plays it. */
const EXIT_MS = 160;

const icon = { width: 16, height: 16, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true } as const;

const GaugeIcon = (): React.JSX.Element => (
  <svg {...icon}>
    <path d="M4.5 17a8.5 8.5 0 1 1 15 0" />
    <path d="M12 13.5 15.5 9" />
    <circle cx="12" cy="14" r="1" />
  </svg>
);

const CalendarIcon = (): React.JSX.Element => (
  <svg {...icon}>
    <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
    <path d="M3.5 10h17M8 3v4M16 3v4" />
  </svg>
);

const GearIcon = (): React.JSX.Element => (
  <svg {...icon}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  </svg>
);

const LogoutIcon = (): React.JSX.Element => (
  <svg {...icon}>
    <path d="M10 4H6.5A2.5 2.5 0 0 0 4 6.5v11A2.5 2.5 0 0 0 6.5 20H10" />
    <path d="M15 8l4 4-4 4M19 12H9.5" />
  </svg>
);

const ComputerIcon = (): React.JSX.Element => (
  <svg {...icon}>
    <rect x="3" y="4" width="18" height="12" rx="2" />
    <path d="M8 20h8M12 16v4" />
  </svg>
);

/** "Mon 15 Sep, 9:00 AM" in the zone the page shows. */
function resetLabel(at: number, zone: string): string {
  try {
    return new Intl.DateTimeFormat(formatLocale(), { timeZone: zone, weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }).format(at);
  } catch {
    return new Date(at).toLocaleString(formatLocale());
  }
}

/** One usage window: how much is used at a glance; opened, the credits behind it and when it starts over. */
function UsageItem({ label, window, glyph, open, onToggle }: { readonly label: string; readonly window: UsageWindow | null; readonly glyph: React.JSX.Element; readonly open: boolean; readonly onToggle: () => void }): React.JSX.Element {
  const t = useText(ACCOUNT_MENU);
  const { zone } = useTimeZone();
  const percent = window == null ? null : percentUsed(window);
  return (
    <div className="context-item-wrap">
      <button type="button" role="menuitem" className="context-item usage-item" aria-expanded={window == null ? undefined : open} onClick={window == null ? undefined : onToggle} disabled={window == null}>
        <span className="context-icon" aria-hidden="true">
          {glyph}
        </span>
        <span className="context-label">{label}</span>
        <span className={`usage-percent${percent != null && percent >= 90 ? " is-high" : ""}`}>{percent == null ? t.noLimit : `${percent}%`}</span>
        {window == null ? null : <span className={`context-more usage-chevron${open ? " open" : ""}`}>›</span>}
      </button>
      {window != null && open ? (
        <div className="usage-detail">
          <div className={`usage-meter${percent! >= 90 ? " is-high" : percent! >= 70 ? " is-tight" : ""}`} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent!} aria-label={`${label}: ${percent}%`}>
            <span style={{ width: `${percent}%` }} />
          </div>
          <span>{t.used(creditsUsed(window))}</span>
          <span>{t.resets(resetLabel(window.resetAt, zone))}</span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The account at the foot of the roster, as the prototype's: the face and
 * name, and on a press the menu above it with the plan's weekly and monthly
 * usage, Settings, and Log out. The plan itself is bought in the platform
 * portal, so there is nothing here to change it. On a box on the user's own
 * computer there is no account: the foot names the computer and opens
 * Settings directly.
 */
export function AccountFoot({ onOpenSettings }: { readonly onOpenSettings: () => void }): React.JSX.Element {
  const t = useText(ACCOUNT_MENU);
  const account = useAccount();
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const menu = usePresence(open, EXIT_MS);
  const username = account?.session.account?.username ?? "";
  const name = account == null ? t.thisComputer : displayName(username) || t.account;

  return (
    <>
      <button
        ref={button}
        type="button"
        className={`account-button${open ? " open" : ""}`}
        aria-haspopup={account == null ? "dialog" : "menu"}
        aria-expanded={account == null ? undefined : open}
        title={account == null ? t.settings : username}
        onClick={() => (account == null ? onOpenSettings() : setOpen((was) => !was))}
      >
        <AccountFace name={account == null ? null : name} />
        <span className="account-name">{name}</span>
      </button>
      {menu.mounted && account != null ? (
        <AccountMenu
          anchor={button.current}
          closing={menu.closing}
          onClose={() => setOpen(false)}
          onOpenSettings={() => {
            setOpen(false);
            onOpenSettings();
          }}
        />
      ) : null}
    </>
  );
}

/** A round face with the name's first letter; a computer where there is no account. */
export function AccountFace({ name, size = 28 }: { readonly name: string | null; readonly size?: number }): React.JSX.Element {
  return (
    <span className="account-face" style={{ width: size, height: size, fontSize: Math.round(size * 0.46) }} aria-hidden="true">
      {name == null ? <ComputerIcon /> : (name.trim()[0] ?? "?").toUpperCase()}
    </span>
  );
}

function AccountMenu({ anchor, closing, onClose, onOpenSettings }: { readonly anchor: HTMLElement | null; readonly closing: boolean; readonly onClose: () => void; readonly onOpenSettings: () => void }): React.JSX.Element | null {
  const t = useText(ACCOUNT_MENU);
  const account = useAccount();
  const [expanded, setExpanded] = useState<"weekly" | "monthly" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [place, setPlace] = useState<{ left: number; bottom: number; width: number } | null>(null);
  // Open is a page of its own to Back, on a phone: it closes the menu rather than the app.
  useBackClose(!closing, onClose);
  useEffect(() => {
    const key = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [onClose]);
  // The meters are read fresh each time the menu opens; the page does not poll for them.
  useEffect(() => {
    void account?.refresh().catch(() => undefined);
    // Once per opening, not on every session the refresh itself brings back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Above the account button, as wide as the foot, and never off the left or right of the window.
  useLayoutEffect(() => {
    if (anchor == null) return;
    const bounds = anchor.getBoundingClientRect();
    const width = Math.max(bounds.width, 248);
    setPlace({ left: Math.max(8, Math.min(bounds.left, window.innerWidth - width - 8)), bottom: window.innerHeight - bounds.top + 6, width });
  }, [anchor]);
  if (account == null) return null;
  const usage = account.session.usage;
  const logout = (): void => {
    setError(null);
    account.logout().catch((cause: unknown) => setError(cause instanceof Error ? cause.message : String(cause)));
  };

  return (
    <>
      {/* The same film as the row menu's (Sidebar.tsx): a tap outside closes the menu and reaches nothing beneath. */}
      <div
        className="context-backdrop"
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
      />
      <div className={`context-menu account-menu${closing ? " closing" : ""}`} role="menu" aria-label={t.account} style={place == null ? { visibility: "hidden" } : { left: place.left, bottom: place.bottom, width: place.width }}>
        {usage != null ? (
          <>
            <UsageItem label={t.weeklyUsage} glyph={<GaugeIcon />} window={usage.weekly} open={expanded === "weekly"} onToggle={() => setExpanded((was) => (was === "weekly" ? null : "weekly"))} />
            <UsageItem label={t.monthlyUsage} glyph={<CalendarIcon />} window={usage.monthly} open={expanded === "monthly"} onToggle={() => setExpanded((was) => (was === "monthly" ? null : "monthly"))} />
          </>
        ) : null}
        <button type="button" role="menuitem" className="context-item" onClick={onOpenSettings}>
          <span className="context-icon" aria-hidden="true">
            <GearIcon />
          </span>
          <span className="context-label">{t.settings}</span>
        </button>
        <hr />
        <button type="button" role="menuitem" className="context-item" onClick={logout}>
          <span className="context-icon" aria-hidden="true">
            <LogoutIcon />
          </span>
          <span className="context-label">{t.logOut}</span>
        </button>
        {error != null ? <div className="account-menu-error" role="alert">{error}</div> : null}
      </div>
    </>
  );
}
